import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow, parseISO } from "date-fns";
import { LuArrowLeft, LuFlag, LuTrash2, LuX } from "react-icons/lu";
import { Heading, Muted } from "@/components/typography";
import {
  ActionBar,
  ActionBarBody,
  ActionBarClose,
  ActionBarContent,
  ActionBarSeparator,
  ActionBarValue,
} from "@/components/ui/action-bar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { Badge } from "@/components/ui/badge";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { toastManager } from "@/components/ui/toast";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  useApproveScript,
  useDeletePage,
  useDeleteScript,
  useFlagScript,
  useReviewScript,
  useSetPageRotation,
  useSplitScript,
} from "../hooks/use-review";
import { useIsDesktop } from "../lib/use-is-desktop";
import { wrapPageIndex } from "../lib/review-helpers";
import type { SaveStatus } from "../lib/use-autosave";
import { pageFileUrl, scriptPdfUrl, type ReviewScriptDetail as ReviewScriptDetailData } from "../lib/review-client";
import { DeleteDialog } from "./delete-dialog";
import { DeletePageDialog } from "./delete-page-dialog";
import { FlagDialog } from "./flag-dialog";
import { ReviewActions } from "./review-actions";
import { ReviewLightbox } from "./review-lightbox";
import { ReviewPageMeta } from "./review-page-meta";
import { ReviewScriptForm } from "./review-script-form";
import { ReviewStage } from "./review-stage";
import { ReviewThumbRail } from "./review-thumb-rail";
import { SplitScriptDialog } from "./split-script-dialog";

const STATUS_BADGE_VARIANT = { pending: "secondary", flagged: "yellow", approved: "green" } as const;

export function ReviewScriptDetail({ scriptId, onBack }: { scriptId: string; onBack: () => void }) {
  const scriptQuery = useReviewScript(scriptId);

  return (
    <div className="flex min-h-full flex-col p-4 md:h-full md:min-h-0 md:p-6">
      <div className="mb-4 flex items-center gap-2">
        <button type="button" onClick={onBack} aria-label="Back" className="text-muted-foreground">
          <LuArrowLeft className="size-5" />
        </button>
        <div>
          <Heading as="h1" variant="h4">
            {scriptQuery.data?.label || scriptId.slice(0, 8)}
          </Heading>
          {scriptQuery.data && <Muted>{scriptQuery.data.pages.length} pages</Muted>}
        </div>
        {scriptQuery.data && (
          <Badge variant={STATUS_BADGE_VARIANT[scriptQuery.data.reviewStatus]} className="ml-2">
            {scriptQuery.data.reviewStatus}
          </Badge>
        )}
      </div>

      <AsyncBoundary query={scriptQuery}>
        <AsyncBoundary.Pending>
          <div className="space-y-3">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} className="h-14 w-full rounded-md" />
            ))}
          </div>
        </AsyncBoundary.Pending>
        <AsyncBoundary.Error>
          {() => (
            <Empty>
              <EmptyTitle>Script gone</EmptyTitle>
              <EmptyDescription>It may have been deleted.</EmptyDescription>
              <Button variant="outline" onClick={onBack} className="mt-2">
                Back to review
              </Button>
            </Empty>
          )}
        </AsyncBoundary.Error>
        <AsyncBoundary.Success>
          {(script: ReviewScriptDetailData) => (
            <DetailBody scriptId={scriptId} script={script} onBack={onBack} />
          )}
        </AsyncBoundary.Success>
      </AsyncBoundary>
    </div>
  );
}

function DetailBody({
  scriptId,
  script,
  onBack,
}: {
  scriptId: string;
  script: ReviewScriptDetailData;
  onBack: () => void;
}) {
  const [selectedPageId, setSelectedPageId] = useState<string | null>(script.pages[0]?.id ?? null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [pageNotesOpen, setPageNotesOpen] = useState(false);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePageOpen, setDeletePageOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [splitOpen, setSplitOpen] = useState(false);

  const approve = useApproveScript(scriptId);
  const flag = useFlagScript(scriptId);
  const deleteScript = useDeleteScript();
  const deletePage = useDeletePage(scriptId);
  const setPageRotation = useSetPageRotation(scriptId);
  const split = useSplitScript(scriptId);
  const navigate = useNavigate();

  const formAutosave = useRef<{ status: SaveStatus; flush: () => Promise<void> } | null>(null);
  const pageAutosave = useRef<{ status: SaveStatus; flush: () => Promise<void> } | null>(null);

  const isDesktop = useIsDesktop();

  const selectedIndex = script.pages.findIndex(p => p.id === selectedPageId);
  const selectedPage = selectedIndex === -1 ? undefined : script.pages[selectedIndex];

  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const liveIds = new Set(script.pages.map(p => p.id));
      const pruned = new Set([...prev].filter(id => liveIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [script.pages]);

  const selectedCount = selectedIds.size;
  const wouldEmpty = selectedCount > 0 && selectedCount === script.pages.length;

  function toggleSelect(pageId: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function exitSelectMode() {
    setSelectMode(false);
    clearSelection();
  }

  const flushAll = useCallback(async () => {
    await Promise.all([formAutosave.current?.flush() ?? Promise.resolve(), pageAutosave.current?.flush() ?? Promise.resolve()]);
  }, []);

  function stepPage(delta: number) {
    if (script.pages.length === 0) return;
    const nextIndex = wrapPageIndex(selectedIndex === -1 ? 0 : selectedIndex, script.pages.length, delta);
    setSelectedPageId(script.pages[nextIndex]?.id ?? null);
  }

  const dirtyFailed = formAutosave.current?.status === "error" || pageAutosave.current?.status === "error";
  const saving = formAutosave.current?.status === "saving" || pageAutosave.current?.status === "saving";
  const actionsDisabled = dirtyFailed || saving;

  async function onApprove() {
    await flushAll();
    try {
      await approve.mutateAsync();
      onBack();
    } catch {
      toastManager.add({ type: "error", title: "Couldn't approve script" });
    }
  }

  async function onFlagSubmit(message: string) {
    await flushAll();
    try {
      await flag.mutateAsync(message);
      setFlagDialogOpen(false);
    } catch {
      toastManager.add({ type: "error", title: "Couldn't flag script" });
    }
  }

  async function onDeletePageConfirm() {
    if (!selectedPage) return;
    const removingId = selectedPage.id;
    const idx = script.pages.findIndex(p => p.id === removingId);
    const next = script.pages[idx + 1] ?? script.pages[idx - 1] ?? null;
    try {
      await deletePage.mutateAsync(removingId);
      setSelectedPageId(next?.id ?? null);
      setDeletePageOpen(false);
      setLightboxOpen(false);
      setPageNotesOpen(false);
    } catch {
      toastManager.add({ type: "error", title: "Couldn't remove page" });
    }
  }

  async function onDeleteConfirm() {
    try {
      await deleteScript.mutateAsync(scriptId);
      setDeleteDialogOpen(false);
      onBack();
    } catch {
      toastManager.add({ type: "error", title: "Couldn't delete script" });
    }
  }

  async function onSplitConfirm() {
    const pageIds = script.pages.filter(p => selectedIds.has(p.id)).map(p => p.id);
    try {
      const res = await split.mutateAsync(pageIds);
      setSplitOpen(false);
      exitSelectMode();
      if (selectedPageId && pageIds.includes(selectedPageId)) {
        const remaining = script.pages.filter(p => !pageIds.includes(p.id));
        setSelectedPageId(remaining[0]?.id ?? null);
      }
      toastManager.add({
        type: "success",
        title: "Created script",
        actionProps: {
          children: "Open",
          onClick: () => {
            navigate({ to: "/review/$scriptId", params: { scriptId: res.newScriptId } });
          },
        },
      });
    } catch {
      toastManager.add({ type: "error", title: "Couldn't create script" });
    }
  }

  const actions = (
    <ReviewActions
      disabled={actionsDisabled}
      approving={approve.isPending}
      onApprove={onApprove}
      onFlag={() => setFlagDialogOpen(true)}
      onDelete={() => setDeleteDialogOpen(true)}
      pdfUrl={scriptPdfUrl(scriptId)}
      pdfDisabled={script.pages.length === 0}
    />
  );

  const form = <ReviewScriptForm script={script} onAutosaveChange={c => (formAutosave.current = c)} />;

  const flagBanner = script.reviewStatus === "flagged" && script.flagMessage && (
    <Alert variant="warning">
      <LuFlag />
      <AlertTitle>Flagged</AlertTitle>
      <AlertDescription>
        {script.flagMessage}
        <Muted>
          {script.flagAckedAt
            ? `Resolved by scanner ${formatDistanceToNow(parseISO(script.flagAckedAt), { addSuffix: true })}`
            : "Not yet seen by scanner"}
        </Muted>
        {script.flagAckedAt && (
          <Muted>{script.flagReply?.trim() ? script.flagReply : "No note"}</Muted>
        )}
      </AlertDescription>
    </Alert>
  );

  const removePageButton = selectedPage && (
    <Button
      variant="destructive-outline"
      className="w-full"
      disabled={deletePage.isPending}
      onClick={() => setDeletePageOpen(true)}
    >
      <LuTrash2 />
      Remove page {selectedIndex + 1}
    </Button>
  );

  const selectToggle = (
    <Button
      type="button"
      size="sm"
      variant={selectMode ? "secondary" : "outline"}
      onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
    >
      {selectMode ? "Done" : "Select pages"}
    </Button>
  );

  const thumbRail = (
    <ReviewThumbRail
      scriptId={scriptId}
      pages={script.pages}
      selectedPageId={selectedPageId}
      onSelect={id => {
        setSelectedPageId(id);
        if (!isDesktop && !selectMode) setLightboxOpen(true);
      }}
      layout={isDesktop ? "list" : "grid"}
      selectMode={selectMode}
      selectedIds={selectedIds}
      onToggleSelect={toggleSelect}
    />
  );

  const thumbColumn = (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {selectToggle}
      <div className="min-h-0 flex-1 overflow-y-auto">{thumbRail}</div>
    </div>
  );

  const pageMeta = selectedPage && (
    <ReviewPageMeta
      key={selectedPage.id}
      scriptId={scriptId}
      page={selectedPage}
      onAutosaveChange={c => (pageAutosave.current = c)}
    />
  );

  return (
    <div className={isDesktop ? "min-h-0 flex-1" : undefined}>
      {isDesktop ? (
        <div className="flex h-full min-h-0 gap-4">
          <div className="flex h-full min-h-0 w-40 shrink-0 flex-col">{thumbColumn}</div>
          <div className="min-w-0 flex-1 rounded-lg bg-background/60">
            <ReviewStage page={selectedPage} onExpand={() => setLightboxOpen(true)} />
          </div>
          <div className="w-full max-w-md shrink-0 space-y-6 overflow-y-auto pl-1">
            {flagBanner}
            {actions}
            {form}
            {pageMeta}
            {removePageButton}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex shrink-0 flex-col gap-2">
            {selectToggle}
            {thumbRail}
          </div>
          {flagBanner}
          {form}
          {selectedPage && (
            <Button variant="outline" onClick={() => setPageNotesOpen(true)}>
              Page notes
            </Button>
          )}
          {removePageButton}
          <div className="sticky bottom-0 -mx-4 border-t bg-background/95 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] backdrop-blur-sm">
            {actions}
          </div>
        </div>
      )}

      <ActionBar
        open={selectMode && selectedCount > 0}
        onOpenChange={open => {
          if (!open && !splitOpen) clearSelection();
        }}
      >
        <ActionBarContent>
          <ActionBarValue count={selectedCount} label="selected" />
          <ActionBarSeparator />
          <ActionBarBody>
            <Button
              type="button"
              disabled={wouldEmpty || split.isPending}
              title={wouldEmpty ? "Keep at least one page" : undefined}
              onClick={() => setSplitOpen(true)}
            >
              Create script
            </Button>
          </ActionBarBody>
          <ActionBarSeparator />
          <ActionBarClose render={<Button size="icon-sm" variant="ghost" />}>
            <LuX />
          </ActionBarClose>
        </ActionBarContent>
      </ActionBar>

      <ReviewLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        src={selectedPage ? pageFileUrl(selectedPage.id, selectedPage.sha256) : null}
        alt="Selected page"
        canPrev={script.pages.length > 1}
        canNext={script.pages.length > 1}
        onPrev={() => stepPage(-1)}
        onNext={() => stepPage(1)}
        rotation={selectedPage?.rotation ?? 0}
        onRotationChange={rotation => {
          if (selectedPage) setPageRotation.mutate({ pageId: selectedPage.id, rotation });
        }}
      />

      {!isDesktop && selectedPage && (
        <Drawer open={pageNotesOpen} onOpenChange={setPageNotesOpen} position="bottom">
          <DrawerPopup showBar className="max-h-[80dvh]">
            <DrawerHeader>
              <DrawerTitle>Page {selectedIndex + 1} of {script.pages.length}</DrawerTitle>
            </DrawerHeader>
            <DrawerPanel>{pageMeta}</DrawerPanel>
            <DrawerFooter>
              <Button className="w-full" onClick={() => setPageNotesOpen(false)}>
                Done
              </Button>
            </DrawerFooter>
          </DrawerPopup>
        </Drawer>
      )}

      <FlagDialog
        open={flagDialogOpen}
        onOpenChange={setFlagDialogOpen}
        onSubmit={onFlagSubmit}
        submitting={flag.isPending}
      />
      <DeletePageDialog
        open={deletePageOpen}
        onOpenChange={setDeletePageOpen}
        onConfirm={onDeletePageConfirm}
        deleting={deletePage.isPending}
        pageLabel={`page ${selectedIndex + 1}`}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={onDeleteConfirm}
        deleting={deleteScript.isPending}
      />
      <SplitScriptDialog
        open={splitOpen}
        onOpenChange={setSplitOpen}
        onConfirm={onSplitConfirm}
        pending={split.isPending}
        pageCount={selectedIds.size}
      />
    </div>
  );
}
